import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import InvestorActivityTable, {
  type InvestorActivityRow,
} from '../components/admin/InvestorActivityTable';

type Totals = {
  startups: number;
  investors: number;
  users: number;
  countries: number;
};

type ActivityResponse = {
  success?: boolean;
  error?: string;
  totals?: Totals;
  investors?: InvestorActivityRow[];
};

const EMPTY_TOTALS: Totals = { startups: 0, investors: 0, users: 0, countries: 0 };

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<Totals>(EMPTY_TOTALS);
  const [rows, setRows] = useState<InvestorActivityRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const getInvestorActivity = httpsCallable(functions, 'getInvestorActivity');
        const result = await getInvestorActivity({});
        const data = result.data as ActivityResponse;
        if (cancelled) return;
        if (!data?.success) {
          setError(data?.error || 'Could not load investor activity.');
          return;
        }
        setTotals(data.totals || EMPTY_TOTALS);
        setRows(data.investors || []);
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Could not load investor activity.';
          setError(
            message.includes('not-found') || message.includes('NOT_FOUND')
              ? 'Deploy getInvestorActivity to load live activity.'
              : message
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#212121] flex flex-col items-center pt-8 pb-16">
      <div className="w-full max-w-7xl px-4 pt-4">
        <header className="mb-6 text-center">
          <h1 className="text-3xl font-semibold text-white">Dashboard</h1>
          <p className="mt-1 text-gray-400">Investor Activity</p>
        </header>

        <div className="flex flex-wrap justify-center gap-3 mb-10">
          <button
            onClick={() => navigate('/users')}
            className="px-8 py-2.5 bg-[#333333] border border-[#f0f0f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            See users
          </button>
          <button
            onClick={() => navigate('/users?tab=pending-approval')}
            className="px-8 py-2.5 bg-[#333333] border border-[#f0f0f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            Pending Approval
          </button>
          <button
            onClick={() => navigate('/admin/promocodes')}
            className="px-8 py-2.5 bg-[#333333] border border-[#f0f0f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            Generate codes
          </button>
          <button
            onClick={() => navigate('/admindashboard')}
            className="px-8 py-2.5 bg-[#333333] border border-[#02b1f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            Stats
          </button>
          <button
            onClick={() => navigate('/admin/partners')}
            className="px-8 py-2.5 bg-[#333333] border border-[#f0f0f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            Partners
          </button>
          <button
            onClick={() => navigate('/near-matches')}
            className="px-8 py-2.5 bg-[#333333] border border-[#f0f0f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            Near Matches
          </button>
          <button
            onClick={() => navigate('/admin/deals')}
            className="px-8 py-2.5 bg-[#333333] border border-[#f0f0f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            Deals
          </button>
          <button
            onClick={() => navigate('/admin/lisa')}
            className="px-8 py-2.5 bg-[#333333] border border-[#f0f0f0] rounded-xl text-white text-sm font-medium hover:bg-[#444444] transition-all"
          >
            LISA preferences
          </button>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="rounded-2xl border border-[#5c5c5c] bg-[#2b2b2b] px-8 py-5 text-[15px] leading-relaxed text-white">
            <p>
              Startups (Total): <span className="text-[#9fd8d0]">{totals.startups}</span>
            </p>
            <p className="mt-1.5">
              Investors (Total): <span className="text-[#9fd8d0]">{totals.investors}</span>
            </p>
          </div>
          <div className="rounded-2xl border border-[#5c5c5c] bg-[#2b2b2b] px-8 py-5 text-[15px] leading-relaxed text-white">
            <p>
              Users (Total): <span className="text-[#9fd8d0]">{totals.users}</span>
            </p>
            <p className="mt-1.5">
              Countries (Total): <span className="text-[#9fd8d0]">{totals.countries}</span>
            </p>
          </div>
        </div>

        <section>
          {!loading && !error && rows.length > 0 ? (
            <p className="mb-4 text-sm text-white">
              Latest visit on a phone:{' '}
              <span className="text-[#9fd8d0]">
                {rows.filter(row => row.device === 'phone').length}
              </span>
              {' · '}
              Profile on a phone:{' '}
              <span className="text-[#9fd8d0]">
                {rows.filter(row => (row.phoneProfileNames?.length ?? 0) > 0).length}
              </span>
              {' · '}
              Pitch deck on a phone:{' '}
              <span className="text-[#9fd8d0]">
                {rows.filter(row => (row.phonePitchDeckNames?.length ?? 0) > 0).length}
              </span>
            </p>
          ) : null}
          {loading ? (
            <p className="text-gray-400">Loading investor activity…</p>
          ) : error ? (
            <p className="text-amber-300">{error}</p>
          ) : (
            <InvestorActivityTable rows={rows} />
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
